// src/pages/CubePage_2.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactSelect from "@/components/common/ReactSelect";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import { apiCall } from "@/api/apiCall";
import {
  MDX_QUERY_ENDPOINT,
  flattenMdxV2Data,
  isMdxV2Response,
  mdxPercentColumnKey,
} from "@/lib/mdxQuery";
import { usePermissions } from "@/context/PermissionContext";
import { toast } from "sonner";
import {
  GripVertical,
  Check,
  X as XIcon,
  Search,
  Filter,
  ChevronRight,
  ChevronLeft,
  Database,
  Table,
  Layers,
  Info,
  BarChart,
  Settings,
  ChevronDown,
  Edit2,
  Plus,
  Calculator,
  Trash2,
} from "lucide-react";
import PivotViewStore from "@/components/child/PivotViewStore";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "@/assets/css/AgGridReact.css";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/components/theme-provider";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * AG Grid Feature Configuration
 */
const DEFAULT_GRID_FEATURES = {
  rowSelection: true,
  singleClickEdit: false,
  pagination: false,
  filtering: true,
  sorting: true,
  columnMenu: true,
  contextMenu: true,
  rangeSelection: true,
  clipboardEnabled: true,
  charts: true,
  statusBar: true,
  sideBar: true,
  rowGrouping: true,
  pivoting: false,
  aggregation: false,
  exportExcel: false,
  exportCSV: false,
};

// Safe field name helper — dots break AG Grid field names
const safeField = (str) => String(str).replace(/\./g, "_DOT_");

// ─────────────────────────────────────────────
// BLANK ROW DETECTION
// ─────────────────────────────────────────────
const isBlankValue = (v) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "");

// Row keys that are not measure values: row id, rowspan / hierarchy info, row dimensions
// (dim_*), and calculated columns (calc_*) — the latter substitute 0 for blank measures,
// so never blank.
const isNonMeasureKey = (key) =>
  key === "id" ||
  key === "_rowspans" ||
  key === "_hierarchy" ||
  key.startsWith("dim_") ||
  key.startsWith("calc_");

// A row is blank when it has measure values and every one of them is blank
const isBlankDataRow = (data) => {
  const measureKeys = Object.keys(data || {}).filter((key) => !isNonMeasureKey(key));
  return measureKeys.length > 0 && measureKeys.every((key) => isBlankValue(data[key]));
};

// ─────────────────────────────────────────────
// ROW HIERARCHY (parent → children within a row dimension)
// ─────────────────────────────────────────────
const HIERARCHY_INDENT_PX = 16;

// A row is hidden when any of its hierarchy ancestors is collapsed
const isUnderCollapsedRow = (data, collapsedKeys) =>
  collapsedKeys.size > 0 &&
  (data?._hierarchy?.ancestorKeys || []).some((key) => collapsedKeys.has(key));

// Hierarchy info of a grid row (innermost row dimension); AG Grid group rows have none
const getNodeHierarchy = (node) => (node?.group ? null : node?.data?._hierarchy ?? null);

// Number of row dimensions on a row (dim_0, dim_1, …)
const getRowDimensionCount = (node) =>
  Object.keys(node?.data || {}).filter((key) => /^dim_\d+$/.test(key)).length;

// Re-nests a sorted list so every hierarchy child follows its parent again,
// with siblings in their sorted order at every level
const nestHierarchy = (nodes) => {
  if (!nodes.some((node) => getNodeHierarchy(node)?.level > 0)) return nodes;

  const keysInList = new Set(nodes.map((node) => getNodeHierarchy(node)?.key).filter(Boolean));
  const childrenByParent = new Map(); // parent key → children, in sorted order
  const roots = [];

  nodes.forEach((node) => {
    // Nearest ancestor present in this list (an ancestor may be absent, e.g. no own values)
    const ancestors = getNodeHierarchy(node)?.ancestorKeys || [];
    const parentKey = [...ancestors].reverse().find((key) => keysInList.has(key));
    if (!parentKey) {
      roots.push(node);
      return;
    }
    if (!childrenByParent.has(parentKey)) childrenByParent.set(parentKey, []);
    childrenByParent.get(parentKey).push(node);
  });

  const ordered = [];
  const appendWithChildren = (node) => {
    ordered.push(node);
    const key = getNodeHierarchy(node)?.key;
    const children = key ? childrenByParent.get(key) : null;
    if (!children) return;
    childrenByParent.delete(key); // guard against a key appearing twice in the list
    children.forEach(appendWithChildren);
  };
  roots.forEach(appendWithChildren);
  return ordered;
};

// Keeps each outer row-dimension member (e.g. a Contract) as one block, so sorting only
// reorders rows inside it. Blocks keep their original order unless that dimension's own
// column is the one being sorted.
const orderByOuterDimensions = (nodes, level, dimCount, sortedColIds) => {
  if (level >= dimCount - 1) return nestHierarchy(nodes);

  const field = `dim_${level}`;
  const blocks = new Map(); // member → rows, in sorted order
  nodes.forEach((node) => {
    const member = node.data[field];
    if (!blocks.has(member)) blocks.set(member, []);
    blocks.get(member).push(node);
  });

  const orderedBlocks = [...blocks.values()];
  if (!sortedColIds.has(field)) {
    const firstSourceIndex = (block) => Math.min(...block.map((node) => node.sourceRowIndex ?? 0));
    orderedBlocks.sort((a, b) => firstSourceIndex(a) - firstSourceIndex(b));
  }
  return orderedBlocks.flatMap((block) =>
    orderByOuterDimensions(block, level + 1, dimCount, sortedColIds)
  );
};

// AG Grid postSortRows: keeps sorting inside the row structure. The grid sorts each sibling
// list (all rows, or a group's leaf rows); this regroups it by outer row dimensions and
// re-nests the hierarchy, so only siblings change order. Lists of group rows are untouched.
const keepHierarchyOnSort = (params) => {
  const nodes = params.nodes;
  if (nodes.length < 2 || nodes.some((node) => node.group || !node.data)) return;

  const sortedColIds = new Set(
    (params.api?.getColumnState?.() || []).filter((col) => col.sort).map((col) => col.colId)
  );
  const ordered = orderByOuterDimensions(nodes, 0, getRowDimensionCount(nodes[0]), sortedColIds);
  nodes.splice(0, nodes.length, ...ordered);
};

// True when two rows share every row-dimension member up to dimIndex
const isSameOuterBlock = (nodeA, nodeB, dimIndex) => {
  if (!nodeA?.data || !nodeB?.data || nodeA.group || nodeB.group) return false;
  for (let i = 0; i <= dimIndex; i++) {
    if (nodeA.data[`dim_${i}`] !== nodeB.data[`dim_${i}`]) return false;
  }
  return true;
};

// Merge info for an outer row-dimension cell, from the grid's CURRENT display order (after
// sort / filter / collapse): the first row of a block spans the whole block, the rest hide.
// Legacy rowSpan is read when a row is rendered, so the grid redraws rows on every model
// update (see onModelUpdated in CubePage).
const getOuterBlockSpan = ({ node, api }, dimIndex) => {
  const index = node?.rowIndex;
  if (index == null || !api) return { isFirst: true, span: 1 };
  if (index > 0 && isSameOuterBlock(api.getDisplayedRowAtIndex(index - 1), node, dimIndex)) {
    return { isFirst: false, span: 0 };
  }
  let span = 1;
  while (isSameOuterBlock(api.getDisplayedRowAtIndex(index + span), node, dimIndex)) span++;
  return { isFirst: true, span };
};

// Innermost row-dimension cell: indents children under their parent and gives parent rows
// an expand/collapse chevron. Collapse state lives in the grid context (see CubePage).
const HierarchyCellRenderer = (params) => {
  const hierarchy = params.data?._hierarchy;
  if (!hierarchy) return params.valueFormatted ?? params.value ?? null;

  const isCollapsed = params.context?.isRowCollapsed?.(hierarchy.key);
  return (
    <span
      className="flex items-center gap-1 h-full min-w-0"
      style={{ paddingLeft: hierarchy.level * HIERARCHY_INDENT_PX }}
    >
      {hierarchy.hasChildren ? (
        <button
          type="button"
          className="inline-flex items-center justify-center w-4 h-4 shrink-0 rounded hover:bg-black/10 dark:hover:bg-white/10"
          aria-label={isCollapsed ? "Expand" : "Collapse"}
          aria-expanded={!isCollapsed}
          onClick={(e) => {
            e.stopPropagation();
            params.context?.toggleRowCollapse?.(hierarchy.key, params.node);
          }}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
      ) : (
        <span className="inline-block w-4 shrink-0" />
      )}
      <span className="truncate">{params.valueFormatted ?? params.value}</span>
    </span>
  );
};

// ─────────────────────────────────────────────
// DYNAMIC CHART
// ─────────────────────────────────────────────
// Hidden column (added by generateColumnDefs for 2+ row dimensions) holding the combined label
const CHART_CATEGORY_COL_ID = "__chartCategory";

const compactNumberFormat = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const formatCompactNumber = (value) =>
  typeof value === "number" ? compactNumberFormat.format(value) : String(value ?? "");

const CHART_LABEL_MAX_LENGTH = 24;
const truncateChartLabel = (value) => {
  const text = String(value ?? "");
  return text.length > CHART_LABEL_MAX_LENGTH
    ? `${text.slice(0, CHART_LABEL_MAX_LENGTH - 1)}…`
    : text;
};

// Builds the range chart from what the grid actually displays (rows after filter/sort/grouping,
// visible columns), so it always matches the table — no hardcoded fields or rowData indexes.
const createDynamicChart = (api) => {
  if (!api || api.isDestroyed?.()) return null;

  // Row groups (2+ row dimensions) — expand so leaf rows with values are charted
  if ((api.getRowGroupColumns?.() || []).length > 0) api.expandAll();

  // Category = combined label of all row dimensions; with a single row dimension, that dim_* itself
  const dimColIds = (api.getColumns() || [])
    .map((col) => col.getColId())
    .filter((id) => id.startsWith("dim_"))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)));
  const categoryColId = api.getColumn(CHART_CATEGORY_COL_ID)
    ? CHART_CATEGORY_COL_ID
    : (dimColIds[dimColIds.length - 1] ?? null);

  const valueColIds = api
    .getAllDisplayedColumns()
    .filter((col) => col.getColDef().chartDataType === "series")
    .map((col) => col.getColId());
  const rowCount = api.getDisplayedRowCount();

  if (valueColIds.length === 0 || rowCount === 0) return null;

  return (
    api.createRangeChart({
      chartType: "groupedColumn",
      cellRange: {
        rowStartIndex: 0,
        rowEndIndex: rowCount - 1,
        columns: [...(categoryColId ? [categoryColId] : []), ...valueColIds],
      },
      chartThemeOverrides: {
        common: {
          padding: { top: 16, right: 24, bottom: 12, left: 24 },
          title: {
            enabled: true,
            text: "Pivot Table Overview Chart",
            fontSize: 15,
            fontWeight: "bold",
          },
          legend: { position: "bottom", spacing: 16 },
          axes: {
            number: {
              // 40000000 → 40M; negatives keep their sign
              label: { formatter: ({ value }) => formatCompactNumber(value) },
              gridLine: { style: [{ stroke: "rgba(127, 127, 127, 0.18)", lineDash: [4, 4] }] },
            },
            category: {
              // Long combined labels (e.g. "Fines & Penalties - All Currency - KSA") are
              // shortened on the axis; the tooltip still shows the full label
              label: {
                rotation: 325,
                avoidCollisions: true,
                minSpacing: 6,
                formatter: ({ value }) => truncateChartLabel(value),
              },
              paddingInner: 0.3,
              groupPaddingInner: 0.08,
            },
          },
        },
        column: {
          series: {
            cornerRadius: 3,
            strokeWidth: 0,
            tooltip: {
              renderer: ({ title, xValue, yValue }) => ({
                title: String(xValue ?? "-"),
                content: `${title}: ${formatCellValue(typeof yValue === "number" ? yValue : null)}`,
              }),
            },
          },
        },
      },
    }) ?? null
  );
};

// ─────────────────────────────────────────────
// EXCEL STYLES
// ─────────────────────────────────────────────
const B_THIN = { lineStyle: "Continuous", weight: 1, color: "#BFC9D4" };
const B_WHITE = { lineStyle: "Continuous", weight: 1, color: "#FFFFFF" };
const B_MED = { lineStyle: "Continuous", weight: 2, color: "#7F96B2" };
const B_AMBER = { lineStyle: "Continuous", weight: 1, color: "#FCD34D" };

const EXCEL_STYLES = [
  {
    id: "excel-dim-header",
    font: { bold: true, color: "#FFFFFF", size: 12 },
    interior: { color: "#000000", pattern: "Solid" },
    alignment: { horizontal: "Center", vertical: "Center", wrapText: false },
    borders: {
      borderBottom: { lineStyle: "Continuous", weight: 2, color: "#FFFFFF" },
      borderTop: { lineStyle: "Continuous", weight: 2, color: "#FFFFFF" },
      borderLeft: { lineStyle: "Continuous", weight: 2, color: "#FFFFFF" },
      borderRight: { lineStyle: "Continuous", weight: 2, color: "#FFFFFF" },
    },
  },
  {
    id: "excel-group-header",
    font: { bold: true, color: "#FFFFFF", size: 11 },
    interior: { color: "#000000", pattern: "Solid" },
    alignment: { horizontal: "Center", vertical: "Center" },
    borders: {
      borderBottom: B_WHITE,
      borderTop: B_WHITE,
      borderLeft: B_WHITE,
      borderRight: B_WHITE,
    },
  },
  {
    id: "excel-measure-header",
    font: { bold: true, color: "#FFFFFF", size: 10 },
    interior: { color: "#000000", pattern: "Solid" },
    alignment: { horizontal: "Center", vertical: "Center" },
    borders: {
      borderBottom: B_WHITE,
      borderTop: B_WHITE,
      borderLeft: B_WHITE,
      borderRight: B_WHITE,
    },
  },
  {
    id: "excel-calc-header",
    font: { bold: true, color: "#FFFFFF", size: 10 },
    interior: { color: "#000000", pattern: "Solid" },
    alignment: { horizontal: "Center", vertical: "Center" },
    borders: {
      borderBottom: B_WHITE,
      borderTop: B_WHITE,
      borderLeft: B_WHITE,
      borderRight: B_WHITE,
    },
  },
  {
    id: "excel-dim-cell",
    font: { bold: true, color: "#000000", size: 11 },
    interior: { color: "#FFFFFF", pattern: "Solid" },
    alignment: { horizontal: "Left", vertical: "Center" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-negative",
    font: { color: "#C00000", size: 10 },
    interior: { color: "#FFF2F2", pattern: "Solid" },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-positive",
    font: { color: "#000000", size: 10 },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-zero",
    font: { color: "#7F7F7F", size: 10 },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-percent-negative",
    font: { color: "#C00000", italic: true, size: 10 },
    interior: { color: "#FFF2F2", pattern: "Solid" },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-percent-positive",
    font: { color: "#1E6823", italic: true, size: 10 },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-percent-zero",
    font: { color: "#7F7F7F", italic: true, size: 10 },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
  {
    id: "excel-calc-col",
    font: { bold: true, color: "#92400E", size: 10 },
    interior: { color: "#FFFBEB", pattern: "Solid" },
    alignment: { horizontal: "Right" },
    borders: {
      borderBottom: B_AMBER,
      borderTop: B_AMBER,
      borderLeft: B_AMBER,
      borderRight: B_AMBER,
    },
  },
  {
    id: "excel-null",
    font: { color: "#AAAAAA", size: 10 },
    alignment: { horizontal: "Right" },
    borders: { borderBottom: B_THIN, borderTop: B_THIN, borderLeft: B_THIN, borderRight: B_THIN },
  },
];

const truncate2 = (v) => Math.trunc(v * 100) / 100;

const formatCellValue = (value, percentMode = false) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value !== "number") return String(value);
  if (percentMode === true) {
    const truncated = truncate2(value * 100);
    const formatted = Math.abs(truncated).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return value < 0 ? `(${formatted}%)` : `${formatted}%`;
  }
  if (percentMode === "symbol") {
    const truncated = truncate2(value);
    const formatted = Math.abs(truncated).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return value < 0 ? `(${formatted}%)` : `${formatted}%`;
  }
  const truncated = truncate2(value);
  const formatted = Math.abs(truncated).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `(${formatted})` : formatted;
};

const getFormattedExportValue = (params) => {
  const colDef = params.column.getColDef();
  return typeof colDef.valueFormatter === "function"
    ? colDef.valueFormatter({ ...params, data: params.node?.data })
    : params.value;
};

const getColumnDepth = (defs) =>
  !defs || defs.length === 0
    ? 0
    : Math.max(...defs.map((d) => (d.children ? 1 + getColumnDepth(d.children) : 1)));

const dimensionsToPivotData = (dimensions) => {
  if (!Array.isArray(dimensions) || dimensions.length === 0) {
    return { dimensions: [], elements: {} };
  }

  const dimensionsList = [];
  const elementsMap = {};

  dimensions.forEach((dim) => {
    const dimName = dim.dimension || dim.Dimension || dim.Name;
    const mode = dim.mode;

    if (!dimName) return;

    dimensionsList.push(dimName);
    const elements = [];

    const extractElementsFromTree = (node, parentPath = []) => {
      if (!node) return;
      const nodeName = node.name || node.Name || node.key?.match(/\[.*\]\.\[(.*)\]/)?.[1] || "";
      const currentPath = [...parentPath, nodeName];

      if (nodeName) {
        const fullPath = `[${dimName}].[${nodeName}]`;
        elements.push({
          name: nodeName,
          fullPath,
          caption: node.caption || nodeName,
          fullName: node.fullName || null,
          attribute: node.attributes?.Caption || node.caption || nodeName,
          employee: node.attributes?.["Employee Name"] || node.attributes?.Employee_Name || null,
          level: currentPath.length - 1,
          path: currentPath,
          children: node.children || [],
        });
      }

      if (Array.isArray(node.children) && node.children.length > 0) {
        node.children.forEach((child) => extractElementsFromTree(child, currentPath));
      }
    };

    if (mode === "leaf") {
      if (Array.isArray(dim.members)) {
        dim.members.forEach((member) => {
          const memberName = member.name || member.key?.match(/\[.*\]\.\[(.*)\]/)?.[1] || "";
          elements.push({
            name: memberName,
            fullPath: `[${dimName}].[${memberName}]`,
            caption: member.caption || memberName,
            fullName: member.fullName || null,
            attribute: member.attributes?.Caption || member.caption || memberName,
            employee:
              member.attributes?.["Employee Name"] || member.attributes?.Employee_Name || null,
            level: 0,
            path: [memberName],
            children: [],
          });
        });
      }
    } else if (mode === "tree") {
      if (Array.isArray(dim.tree)) {
        dim.tree.forEach((treeNode) => extractElementsFromTree(treeNode, []));
      }
      if (Array.isArray(dim.unlinked)) {
        dim.unlinked.forEach((unlinkedItem) => {
          const unlinkedName =
            unlinkedItem.name || unlinkedItem.key?.match(/\[.*\]\.\[(.*)\]/)?.[1] || "";
          elements.push({
            name: unlinkedName,
            fullPath: `[${dimName}].[${unlinkedName}]`,
            caption: unlinkedItem.caption || unlinkedName,
            fullName: unlinkedItem.fullName || null,
            attribute: unlinkedItem.attributes?.Caption || unlinkedItem.caption || unlinkedName,
            employee:
              unlinkedItem.attributes?.["Employee Name"] ||
              unlinkedItem.attributes?.Employee_Name ||
              null,
            level: 0,
            path: [unlinkedName],
            children: [],
          });
        });
      }
    } else {
      if (Array.isArray(dim.members)) {
        dim.members.forEach((member) => {
          const memberName = member.name || member.key?.match(/\[.*\]\.\[(.*)\]/)?.[1] || "";
          elements.push({
            name: memberName,
            fullPath: `[${dimName}].[${memberName}]`,
            caption: member.caption || memberName,
            fullName: member.fullName || null,
            attribute: member.attributes?.Caption || member.caption || memberName,
            employee:
              member.attributes?.["Employee Name"] || member.attributes?.Employee_Name || null,
            level: 0,
            path: [memberName],
            children: [],
          });
        });
      }
    }

    elementsMap[dimName] = elements;
  });

  return { dimensions: dimensionsList, elements: elementsMap };
};

const extractMeasures = (rows) => {
  if (rows.length === 0) return [];
  const firstRow = rows[0];
  const firstColumnData = Object.values(firstRow._columns)[0] || {};
  return Object.keys(firstColumnData);
};

const calculateRowSpans = (rows, numLevels) => {
  if (rows.length === 0) return [];
  const displayRows = [];
  rows.forEach((row, rowIndex) => {
    const displayRow = { ...row, _rowspans: {} };
    for (let level = 1; level <= numLevels; level++) {
      const levelKey = `RowLevel_${level}`;
      const currentValue = row[levelKey];
      let shouldDisplay = true;
      let spanCount = 1;

      if (rowIndex > 0) {
        const prevRow = rows[rowIndex - 1];
        let allPrevLevelsMatch = true;
        for (let prevLevel = 1; prevLevel < level; prevLevel++) {
          if (prevRow[`RowLevel_${prevLevel}`] !== row[`RowLevel_${prevLevel}`]) {
            allPrevLevelsMatch = false;
            break;
          }
        }
        if (allPrevLevelsMatch && prevRow[levelKey] === currentValue) {
          shouldDisplay = false;
        }
      }

      if (shouldDisplay) {
        for (let i = rowIndex + 1; i < rows.length; i++) {
          const nextRow = rows[i];
          let allPrevLevelsMatch = true;
          for (let prevLevel = 1; prevLevel < level; prevLevel++) {
            if (nextRow[`RowLevel_${prevLevel}`] !== row[`RowLevel_${prevLevel}`]) {
              allPrevLevelsMatch = false;
              break;
            }
          }
          if (allPrevLevelsMatch && nextRow[levelKey] === currentValue) {
            spanCount++;
          } else {
            break;
          }
        }
        displayRow._rowspans[levelKey] = spanCount;
      } else {
        displayRow._rowspans[levelKey] = 0;
      }
    }
    displayRows.push(displayRow);
  });
  return displayRows;
};

const convertToAGGridFormat = (mdxData, rowDimensions, columnStructure, measures, useRowSpans) => {
  if (mdxData.length === 0) return [];

  let rowsWithSpans = useRowSpans
    ? calculateRowSpans(mdxData, rowDimensions.length)
    : mdxData.map((row) => ({ ...row, _rowspans: {} }));

  return rowsWithSpans.map((row) => {
    const gridRow = {
      id: row._rowPath.join("-"),
      _rowspans: row._rowspans,
      _hierarchy: row._hierarchy,
    };

    rowDimensions.forEach((dim, index) => {
      gridRow[`dim_${index}`] = row[`RowLevel_${index + 1}`] || "-";
    });

    columnStructure.forEach((colPath) => {
      const colPathKey = colPath.join("|||");
      const cellData = row._columns[colPathKey] || {};
      measures.forEach((measure) => {
        const fieldName = `${colPath.map(safeField).join("_")}_${safeField(measure)}`;
        const value = cellData[measure];
        gridRow[fieldName] = value !== undefined && value !== null ? value : null;
      });
    });

    return gridRow;
  });
};

const generateColumnDefs = (
  rowDimensions,
  columnStructure,
  measures,
  useRowSpans,
  percentRows = [],
  calculatedColumns = [],
  percentColumns = []
) => {
  const columnDefs = [];

  rowDimensions.forEach((dim, index) => {
    const colDef = {
      headerName: dim,
      field: `dim_${index}`,
      width: 200,
      lockPosition: true,
      suppressMovable: true,
      chartDataType: "category",
      pinned: null,
      cellClass: "excel-dim-cell",
      headerClass: "excel-dim-header",
    };

    // Innermost (visible) row dimension shows the parent/child hierarchy
    if (index === rowDimensions.length - 1) {
      colDef.cellRenderer = HierarchyCellRenderer;
    }

    if (rowDimensions.length === 1) {
      colDef.rowSpan = (params) => params.data?._rowspans?.[`RowLevel_${index + 1}`] || 1;
      colDef.cellClassRules = {
        "ag-cell-span": (params) => params.data?._rowspans?.[`RowLevel_${index + 1}`] > 1,
        "ag-cell-span-hidden": (params) => params.data?._rowspans?.[`RowLevel_${index + 1}`] === 0,
      };
      colDef.cellStyle = (params) => {
        const span = params.data?._rowspans?.[`RowLevel_${index + 1}`];
        return span === 0 ? { display: "none" } : {};
      };
    } else if (rowDimensions.length > 1) {
      // Not grouped by default (the user can still drag a dimension into the row-group panel);
      // an outer member shows once, as one merged, centered cell
      if (index < rowDimensions.length - 1) {
        colDef.cellClass = ["excel-dim-cell", "excel-span-dim-cell"];
        colDef.rowSpan = (params) => Math.max(1, getOuterBlockSpan(params, index).span);
        colDef.cellClassRules = {
          "ag-cell-span": (params) => getOuterBlockSpan(params, index).span > 1,
          "ag-cell-span-hidden": (params) => !getOuterBlockSpan(params, index).isFirst,
        };
      }
    }

    columnDefs.push(colDef);
  });

  // Hidden chart-only category combining ALL row dimensions (e.g. "Contract - Currency"),
  // so chart labels are unique per row instead of repeating the innermost dimension only
  if (rowDimensions.length > 1) {
    columnDefs.push({
      colId: CHART_CATEGORY_COL_ID,
      headerName: rowDimensions.join(" / "),
      valueGetter: (params) =>
        params.data
          ? rowDimensions
              .map((_, i) => params.data[`dim_${i}`])
              .filter((v) => v !== null && v !== undefined && v !== "")
              .join(" - ")
          : undefined,
      chartDataType: "category",
      hide: true,
      lockVisible: true,
      lockPosition: true,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      filter: false,
      sortable: false,
      enableRowGroup: false,
      enablePivot: false,
      enableValue: false,
    });
  }

  const buildColumnHierarchy = (paths, level = 0, maxLevel) => {
    const grouped = {};
    paths.forEach((path) => {
      const key = path.slice(0, level + 1).join("|||");
      if (!grouped[key]) grouped[key] = { label: path[level], paths: [] };
      grouped[key].paths.push(path);
    });

    return Object.entries(grouped).flatMap(([key, data]) => {
      if (level === maxLevel - 1) {
        const path = data.paths[0];
        const leafFields = [
          ...measures.map((measure) => {
            // Percent when the column (columns[].isPercent) or the row (data[].isPercent) says so.
            // v2 percent values are already in percent units, so they're shown as-is + "%".
            const isPercentColumn = percentColumns.includes(mdxPercentColumnKey(path, measure));
            const isPercentCell = (params) =>
              isPercentColumn ||
              percentRows.includes(
                rowDimensions.map((_, i) => params.data?.[`dim_${i}`]).join("|||")
              );
            return {
              headerName: measure,
              field: `${path.map(safeField).join("_")}_${safeField(measure)}`,
              width: 130,
              minWidth: 100,
              flex: 1,
              pinned: null,
              suppressMovable: false,
              enablePivot: true,
              enableValue: true,
              // Explicit chart role — otherwise AG Grid infers it from row 0 and a blank first
              // cell turns a measure into a category, dropping it from the chart
              chartDataType: "series",
              headerClass: ["text-[12px]", "excel-measure-header"],
              valueFormatter: (params) =>
                formatCellValue(params.value, isPercentCell(params) ? "symbol" : false),
              cellClassRules: {
                "text-[12px] text-right": () => true,
                "excel-percent-negative": (params) =>
                  isPercentCell(params) && typeof params.value === "number" && params.value < 0,
                "excel-percent-positive": (params) =>
                  isPercentCell(params) && typeof params.value === "number" && params.value > 0,
                "excel-percent-zero": (params) =>
                  isPercentCell(params) &&
                  (params.value === 0 || params.value === null || params.value === undefined),
                "excel-negative": (params) =>
                  !isPercentCell(params) && typeof params.value === "number" && params.value < 0,
                "excel-positive": (params) =>
                  !isPercentCell(params) && typeof params.value === "number" && params.value > 0,
                "excel-zero": (params) => !isPercentCell(params) && params.value === 0,
                "excel-null": (params) => params.value === null || params.value === undefined,
              },
            };
          }),
          ...calculatedColumns.map((col) => ({
            headerName: col.name,
            field: `calc_${col.id}_${path.map(safeField).join("_")}`,
            width: 130,
            minWidth: 100,
            flex: 1,
            chartDataType: "series",
            headerClass: ["text-[12px]", "font-semibold", "excel-calc-header"],
            cellStyle: { background: "rgba(245, 158, 11, 0.06)" },
            valueFormatter: (params) => formatCellValue(params.value),
            cellClassRules: {
              "text-[12px] text-right": () => true,
              "excel-calc-col": () => true,
            },
          })),
        ];

        if (maxLevel === 1 && !data.label) {
          return leafFields;
        }

        return [
          {
            headerName: data.label,
            headerClass: ["text-[12px]", "font-semibold", "p-5", "excel-group-header"],
            children: leafFields,
          },
        ];
      } else {
        return [
          {
            headerName: data.label,
            headerClass: ["text-[12px]", "font-semibold", "excel-group-header"],
            children: buildColumnHierarchy(data.paths, level + 1, maxLevel),
          },
        ];
      }
    });
  };

  const maxLevel = columnStructure.length > 0 ? columnStructure[0].length : 0;
  if (maxLevel > 0) {
    columnDefs.push(...buildColumnHierarchy(columnStructure, 0, maxLevel));
  }

  return columnDefs;
};

const applyCalculatedColumns = (gridData, colDefs, calcCols, colStructure, measures) => {
  if (!calcCols || calcCols.length === 0) return { gridData, colDefs };

  const newGridData = gridData.map((row) => {
    const newRow = { ...row };
    calcCols.forEach((col) => {
      colStructure.forEach((colPath) => {
        const fieldKey = `calc_${col.id}_${colPath.map(safeField).join("_")}`;
        try {
          const evalFormula = col.formula.replace(/\[([^\]]+)\]/g, (match, token) => {
            if (measures.includes(token)) {
              const fieldName = `${colPath.map(safeField).join("_")}_${safeField(token)}`;
              const val = row[fieldName];
              return val !== null && val !== undefined ? val : 0;
            }
            return 0;
          });
          // eslint-disable-next-line no-new-func
          const result = Function(`"use strict"; return (${evalFormula})`)();
          newRow[fieldKey] = isFinite(result) ? result : null;
        } catch {
          newRow[fieldKey] = null;
        }
      });
    });
    return newRow;
  });

  return { gridData: newGridData, colDefs };
};

// ─────────────────────────────────────────────
// CALCULATED COLUMN DIALOG
// ─────────────────────────────────────────────
const CalculatedColumnDialog = ({ open, onClose, onSave, availableFields, editingCol = null }) => {
  const [colName, setColName] = useState("");
  const [formula, setFormula] = useState("");
  const [error, setError] = useState("");
  const formulaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setColName(editingCol?.name || "");
      setFormula(editingCol?.formula || "");
      setError("");
    }
  }, [open, editingCol]);

  const insertAtCursor = (text) => {
    const input = formulaRef.current;
    if (!input) {
      setFormula((prev) => prev + text);
      return;
    }
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const newVal = formula.slice(0, start) + text + formula.slice(end);
    setFormula(newVal);
    setTimeout(() => {
      input.selectionStart = input.selectionEnd = start + text.length;
      input.focus();
    }, 0);
  };

  const insertField = (field) => insertAtCursor(`[${field}]`);
  const insertOperator = (op) => insertAtCursor(` ${op} `);

  const validateFormula = () => {
    if (!colName.trim()) return "Column name is required";
    if (!formula.trim()) return "Formula is required";

    const usedFields = [...formula.matchAll(/\[([^\]]+)\]/g)].map((m) => m[1]);
    const invalidFields = usedFields.filter((f) => !availableFields.includes(f));
    if (invalidFields.length > 0) return `Unknown fields: ${invalidFields.join(", ")}`;

    const testFormula = formula.replace(/\[([^\]]+)\]/g, "1");
    try {
      // eslint-disable-next-line no-new-func
      Function(`"use strict"; return (${testFormula})`)();
    } catch {
      return "Invalid formula syntax";
    }
    return null;
  };

  const handleSave = () => {
    const err = validateFormula();
    if (err) {
      setError(err);
      return;
    }
    onSave({
      id: editingCol?.id || `calc_${Date.now()}`,
      name: colName.trim(),
      formula: formula.trim(),
    });
    onClose();
  };

  const operators = ["+", "-", "*", "/", "(", ")", "%"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Calculator className="w-4 h-4 text-amber-500" />
            {editingCol ? "Edit" : "Add"} Calculated Column
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Column Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={colName}
              onChange={(e) => setColName(e.target.value)}
              placeholder="e.g. Growth %, Variance, YoY"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Available Columns
              <span className="text-xs text-muted-foreground ml-2">
                click to insert into formula
              </span>
            </label>
            <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg bg-muted/30 max-h-28 overflow-y-auto">
              {availableFields.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No columns available</p>
              )}
              {availableFields.map((field) => (
                <button
                  key={field}
                  type="button"
                  onClick={() => insertField(field)}
                  className="text-xs px-2.5 py-1 bg-background hover:bg-primary hover:text-primary-foreground border rounded-md transition-colors font-mono"
                >
                  {field}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Operators</label>
            <div className="flex gap-1.5">
              {operators.map((op) => (
                <button
                  key={op}
                  type="button"
                  onClick={() => insertOperator(op)}
                  className="w-9 h-9 text-sm font-mono font-bold bg-muted hover:bg-primary hover:text-primary-foreground border rounded-md transition-colors"
                >
                  {op}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Formula <span className="text-red-500">*</span>
            </label>
            <Input
              ref={formulaRef}
              value={formula}
              onChange={(e) => {
                setFormula(e.target.value);
                setError("");
              }}
              placeholder="e.g. [ATV] - [ALS]  or  ([ATV] / [ALS] - 1) * 100"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Use measure names in brackets — applied across <strong>all</strong> year/version
              groups.&nbsp; Examples: &nbsp;
              <code className="bg-muted px-1 rounded">[ATV] - [ALS]</code>&nbsp;&nbsp;
              <code className="bg-muted px-1 rounded">([ATV] / [ALS]) * 100</code>&nbsp;&nbsp;
              <code className="bg-muted px-1 rounded">[ATV] / 1000</code>
            </p>
          </div>

          {(colName || formula) && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Preview</p>
              <p className="text-sm font-mono">
                <span className="font-semibold text-amber-700 dark:text-amber-400">
                  {colName || "Column"}
                </span>
                <span className="text-muted-foreground"> = </span>
                <span>{formula || "..."}</span>
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
              <XIcon className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Check className="w-4 h-4 mr-2" />
            {editingCol ? "Update" : "Add"} Column{" "}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────// DRAGGABLE DIMENSION
// ─────────────────────────────────────────────
const DraggableDimension = ({
  dimension,
  onDragStart,
  onFilterClick,
  selectedElements,
  elementsMap,
}) => {
  const selectedFullPath = selectedElements?.[dimension]?.[0];
  const selectedElementLabel = useMemo(() => {
    if (!selectedFullPath) return null;
    const elements = elementsMap?.[dimension] || [];
    const match = elements.find((el) => el.fullPath === selectedFullPath);
    const base = match?.employee || match?.attribute || match?.name || selectedFullPath;
    const total = selectedElements?.[dimension]?.length || 0;
    return total > 1 ? `${base} +${total - 1}` : base;
  }, [selectedFullPath, selectedElements, elementsMap, dimension]);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, dimension)}
      onClick={() => onFilterClick(dimension)}
      className="relative group flex items-center justify-between gap-2 px-2 py-1 bg-secondary rounded-md cursor-move hover:bg-secondary/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200"
    >
      <div className="flex items-center gap-2 min-w-0">
        <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
        {selectedElementLabel && (
          <span className="whitespace-nowrap">
            <span className="text-[10px] text-muted-foreground">{selectedElementLabel}</span>
            <span className="text-[9px] px-1.5 py-0.5 leading-none">
              ({dimension.split(/[-_]/).join(" ").replace(/[{}]/g, "").trim()})
            </span>
          </span>
        )}
      </div>
      <Filter className="w-4 h-4 text-muted-foreground shrink-0" />
    </div>
  );
};

// ─────────────────────────────────────────────
// FILTER DIALOG
// ─────────────────────────────────────────────
const FilterDialog = ({
  dimension,
  elements,
  selectedElements,
  onSave,
  onClose,
  open,
  isInAxis,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelected, setLocalSelected] = useState(new Set(selectedElements));
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const isSingleMode = !isInAxis;

  useEffect(() => {
    if (open) {
      setLocalSelected(new Set(selectedElements));
      setSearchTerm("");
      setExpandedNodes(new Set());
    }
  }, [open]);

  const getAllPathsFromNode = useCallback(
    (node) => {
      const fullPath = node.fullPath || `[${dimension}].[${node.name}]`;
      const paths = [fullPath];
      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => paths.push(...getAllPathsFromNode(child)));
      }
      return paths;
    },
    [dimension]
  );

  const treeData = useMemo(() => {
    return elements.filter((el) => el.level === 0);
  }, [elements]);

  const filterTree = useCallback(
    (node, search) => {
      const fullPath = node.fullPath || `[${dimension}].[${node.name}]`;

      const selfMatches =
        (node.name && node.name.toLowerCase().includes(search)) ||
        (node.caption && node.caption.toLowerCase().includes(search)) ||
        (node.fullName && node.fullName.toLowerCase().includes(search)) ||
        (node.attribute && node.attribute.toLowerCase().includes(search));

      const filteredChildren = (node.children || [])
        .map((child) => filterTree(child, search))
        .filter(Boolean);

      if (selfMatches) return { ...node, fullPath };
      if (filteredChildren.length > 0) return { ...node, fullPath, children: filteredChildren };
      return null;
    },
    [dimension]
  );

  const filteredTree = useMemo(() => {
    if (!searchTerm.trim()) return treeData;
    const search = searchTerm.toLowerCase();
    return treeData.map((node) => filterTree(node, search)).filter(Boolean);
  }, [treeData, searchTerm, filterTree]);

  const handleToggle = (fullPath) => {
    if (isSingleMode) {
      // single mode: radio click = sirf yehi ek select
      setLocalSelected(new Set([fullPath]));
    } else {
      const newSelected = new Set(localSelected);
      newSelected.has(fullPath) ? newSelected.delete(fullPath) : newSelected.add(fullPath);
      setLocalSelected(newSelected);
    }
  };

  const handleToggleExpand = (fullPath) => {
    const newExpanded = new Set(expandedNodes);
    newExpanded.has(fullPath) ? newExpanded.delete(fullPath) : newExpanded.add(fullPath);
    setExpandedNodes(newExpanded);
  };

  // ✅ Sirf DIRECT children (1 level) ke paths — sub-children / grandchildren nahi
  const getDirectChildPaths = useCallback(
    (node) =>
      (node.children || []).map((c) => c.fullPath || `[${dimension}].[${c.name}]`),
    [dimension]
  );

  // ✅ Is node ke direct children select karo (parent aur sub-children untouched)
  const handleSelectDirectChildren = useCallback(
    (node) => {
      const parentPath = node.fullPath || `[${dimension}].[${node.name}]`;
      const paths = getDirectChildPaths(node);
      // single mode me purani selection replace hogi, multi mode me add hogi
      const newSelected = isSingleMode ? new Set() : new Set(localSelected);
      newSelected.add(parentPath); // ✅ parent bhi select hoga
      paths.forEach((p) => newSelected.add(p));
      setLocalSelected(newSelected);
      toast.success(`Selected parent + ${paths.length} direct children`);
    },
    [isSingleMode, localSelected, getDirectChildPaths, dimension]
  );

  // ✅ Is node ke direct children deselect karo
  const handleDeselectDirectChildren = useCallback(
    (node) => {
      const parentPath = node.fullPath || `[${dimension}].[${node.name}]`;
      const paths = getDirectChildPaths(node);
      const newSelected = new Set(localSelected);
      newSelected.delete(parentPath); // ✅ parent bhi deselect
      paths.forEach((p) => newSelected.delete(p));
      setLocalSelected(newSelected);
      toast.success(`Deselected parent + direct children`);
    },
    [localSelected, getDirectChildPaths, dimension]
  );

  const handleSelectAll = () => {
    if (isSingleMode) return;
    const allPaths = treeData.flatMap((node) => getAllPathsFromNode(node));
    setLocalSelected(new Set(allPaths));
  };

  const handleDeselectAll = () => {
    setLocalSelected(new Set());
  };

  const handleSave = () => {
    const selectedArray = Array.from(localSelected);
    onSave(dimension, selectedArray);
    onClose();
  };

  const isPartiallySelected = useCallback(
    (node) => {
      if (isSingleMode || !(localSelected instanceof Set)) return false;
      const paths = getAllPathsFromNode(node);
      const selectedCount = paths.filter((p) => localSelected.has(p)).length;
      return selectedCount > 0 && selectedCount < paths.length;
    },
    [isSingleMode, localSelected, getAllPathsFromNode]
  );

  const totalElements = useMemo(() => {
    const count = (node) => 1 + (node.children || []).reduce((acc, c) => acc + count(c), 0);
    return treeData.reduce((acc, node) => acc + count(node), 0);
  }, [treeData]);

  const TreeNode = ({ node, depth = 0 }) => {
    const fullPath = node.fullPath || `[${dimension}].[${node.name}]`;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = searchTerm.trim() ? true : expandedNodes.has(fullPath);
    const isSelected = localSelected.has(fullPath);
    const isPartial = !isSingleMode && isPartiallySelected(node);

    const displayValue = node.fullName || node.caption || node.name || fullPath;

    return (
      <>
        <div
          className={`group flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors ${
            isSelected ? "bg-primary/5 border border-primary/20" : ""
          }`}
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand(fullPath);
              }}
              className="w-5 h-5 flex items-center justify-center hover:bg-muted rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          {isSingleMode ? (
            <div
              className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center cursor-pointer"
              onClick={() => handleToggle(fullPath)}
            >
              {isSelected && <div className="w-3 h-3 rounded-full bg-primary" />}
            </div>
          ) : (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => handleToggle(fullPath)}
              onClick={(e) => e.stopPropagation()}
              className={
                isPartial && !isSelected
                  ? "data-[state=unchecked]:bg-primary/30 border-primary/50"
                  : ""
              }
            />
          )}

          <div className="flex-1 cursor-pointer" onClick={() => handleToggle(fullPath)}>
            <p className="text-sm font-medium">
              {displayValue}
              {hasChildren && (
                <span className="text-xs text-muted-foreground ml-2">({node.children.length})</span>
              )}
            </p>
          </div>

          {hasChildren && (
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectDirectChildren(node);
                }}
                className="px-2 py-1 text-xs rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 transition-colors"
                title="Select direct children only"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeselectDirectChildren(node);
                }}
                className="px-2 py-1 text-xs rounded bg-red-500/10 hover:bg-red-500/20 text-red-700 dark:text-red-400 transition-colors"
                title="Deselect direct children only"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          )}

          {isSelected && !hasChildren && <Check className="w-4 h-4 text-primary shrink-0" />}
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children.map((child, idx) => (
              <TreeNode
                key={`${child.fullPath || child.name}_${depth}_${idx}`}
                node={child}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
      <DialogHeader>
        <DialogTitle className="text-sm flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filter Elements: <span className="font-bold">{dimension}</span>
          {/* {isSingleMode && (
            <span className="text-xs text-muted-foreground">(Single Select — ✓ icon se direct children)</span>
          )} */}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
        <div className="relative w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search elements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-5 pl-12"
            autoFocus
          />
        </div>

        {!isSingleMode && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleSelectAll} className="flex-1">
              <Check className="w-4 h-4 mr-2 text-emerald-600" />
              Select All ({totalElements})
            </Button>
            <Button size="sm" variant="outline" onClick={handleDeselectAll} className="flex-1">
              <XIcon className="w-4 h-4 mr-2 text-red-600" />
              Deselect All
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto border rounded-md p-3 space-y-1">
          {filteredTree.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No elements found</p>
          ) : (
            filteredTree.map((node, idx) => (
              <TreeNode key={`${node.fullPath || node.name}_root_${idx}`} node={node} />
            ))
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>Apply Filter</Button>
      </div>
    </DialogContent>
  );
};

// ─────────────────────────────────────────────
// DROP ZONE
// ─────────────────────────────────────────────
const DropZone = ({
  title,
  items,
  onDrop,
  onRemove,
  type,
  handleFilterClick,
  onReorder,
  className = "",
  bare = false,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => {
    setIsDragOver(false);
    setDragOverIndex(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragOverIndex(null);
    const dimension = e.dataTransfer.getData("dimension");
    const sourceType = e.dataTransfer.getData("sourceType");
    const sourceIndex = parseInt(e.dataTransfer.getData("sourceIndex"), 10);
    if (!dimension) return;

    if (sourceType === type) {
      const newItems = [...items];
      newItems.splice(sourceIndex, 1);
      const insertIndex =
        dragOverIndex === null
          ? newItems.length
          : dragOverIndex > sourceIndex
            ? dragOverIndex - 1
            : dragOverIndex;
      newItems.splice(insertIndex, 0, dimension);
      onReorder(type, newItems);
      return;
    }

    if (sourceType && sourceType !== type) onRemove(dimension, sourceType, true);

    if (dragOverIndex !== null) {
      const newItems = [...items];
      newItems.splice(dragOverIndex, 0, dimension);
      onReorder(type, newItems);
    } else {
      onDrop(dimension, type);
    }
  };

  const icon = type === "rows" ? <Layers className="w-4 h-4" /> : <Table className="w-4 h-4" />;

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={
        bare
          ? `p-1 transition-all duration-300 ${className} ${isDragOver ? "bg-primary/5 ring-2 ring-inset ring-primary" : ""}`
          : `p-1 border-2 border-dashed rounded-lg transition-all duration-300 ${className} ${isDragOver ? "border-primary bg-primary/5 shadow-lg scale-[1.02]" : "border-border bg-muted/30"}`
      }
    >
      {items.length === 0 && (
        <div className="flex items-center gap-1.5 mb-1.5">
          {icon}
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            {title}
          </h3>
        </div>
      )}
      <div className={type === "columns" ? "flex flex-wrap gap-1.5" : "space-y-1"}>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-2 text-center">
            <p className="text-xs text-muted-foreground italic">Drag dimensions here</p>
          </div>
        ) : (
          items.map((item, index) => (
            <div
              key={item}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("dimension", item);
                e.dataTransfer.setData("sourceType", type);
                e.dataTransfer.setData("sourceIndex", index.toString());
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverIndex(index);
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFilterClick(item);
              }}
              className={`group flex items-center justify-between gap-2 px-2 py-1 bg-secondary rounded-md cursor-move hover:bg-secondary/80 hover:shadow-md hover:scale-[1.02] transition-all duration-200 ${dragOverIndex === index ? "border-2 border-primary" : ""}`}
            >
              <div className="flex items-center gap-1.5">
                <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">
                  {item.split(/[-_]/).join(" ").replace(/_/g, " ").replace(/[{}]/g, "").trim()}
                </span>
              </div>
              <Filter className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// EMPTY STATES
// ─────────────────────────────────────────────
const EmptyStateNoCube = () => (
  <div className="flex items-center justify-center h-full min-h-0">
    <div className="text-center max-w-md mx-auto p-8">
      <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
        <Database className="w-12 h-12 text-muted-foreground" />
      </div>
      <h3 className="text-xl font-bold mb-3">No Cube Selected</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Please select a cube from the top bar to get started with your analysis
      </p>
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Info className="w-4 h-4" />
        <span>Use the cube selector at the top to begin</span>
      </div>
    </div>
  </div>
);

const EmptyStateNoDimensions = () => (
  <div className="flex items-center justify-center h-full min-h-0">
    <div className="text-center max-w-md mx-auto p-8">
      <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
        <Layers className="w-12 h-12 text-muted-foreground" />
      </div>
      <h3 className="text-xl font-bold mb-3">Configure Your Pivot Table</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Drag dimensions from the available dimensions area into the Rows and Columns zones
      </p>
      <div className="space-y-2">
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Layers className="w-4 h-4" />
          <span>
            Drag to <strong>Rows</strong> for vertical grouping
          </span>
        </div>
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Table className="w-4 h-4" />
          <span>
            Drag to <strong>Columns</strong> for horizontal grouping
          </span>
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// GRID FEATURES DIALOG
// ─────────────────────────────────────────────
const GridFeaturesDialog = ({ open, onClose, features, onSave }) => {
  const [localFeatures, setLocalFeatures] = useState(features);

  useEffect(() => {
    if (open) setLocalFeatures(features);
  }, [open, features]);

  const handleToggle = (feature) =>
    setLocalFeatures((prev) => ({ ...prev, [feature]: !prev[feature] }));

  const handleSave = () => {
    onSave(localFeatures);
    onClose();
    toast.success("Grid features updated");
  };

  const featureCategories = [
    {
      category: "Selection & Interaction",
      features: [
        {
          key: "rowSelection",
          label: "Row Selection",
          description: "Enable row selection with checkboxes",
        },
        {
          key: "rangeSelection",
          label: "Range Selection",
          description: "Select multiple cells like Excel",
        },
      ],
    },
    {
      category: "Data Management",
      features: [
        {
          key: "pagination",
          label: "Pagination",
          description: "Enable pagination for large datasets",
        },
        { key: "filtering", label: "Column Filtering", description: "Enable column filter menus" },
        { key: "sorting", label: "Column Sorting", description: "Enable column sorting" },
      ],
    },
    {
      category: "UI Features",
      features: [
        {
          key: "columnMenu",
          label: "Column Menu",
          description: "Right-click on header for options",
        },
        {
          key: "contextMenu",
          label: "Context Menu",
          description: "Right-click context menu on cells",
        },
        { key: "statusBar", label: "Status Bar", description: "Show aggregations and row count" },
      ],
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configure AG Grid Features
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-2">Enable or disable features as needed</p>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {featureCategories.map((category) => (
            <div key={category.category}>
              <h3 className="text-sm font-semibold mb-3 text-primary">{category.category}</h3>
              <div className="space-y-3">
                {category.features.map((feature) => (
                  <div
                    key={feature.key}
                    className="flex items-start justify-between gap-4 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <Label className="text-sm font-medium cursor-pointer">{feature.label}</Label>
                      <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                    </div>
                    <Switch
                      checked={localFeatures[feature.key]}
                      onCheckedChange={() => handleToggle(feature.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setLocalFeatures(DEFAULT_GRID_FEATURES);
              toast.info("Reset to default");
            }}
          >
            Reset to Default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Configuration</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────
// MAIN CUBE PAGE
// ─────────────────────────────────────────────
const CubePage = () => {
  const [loading, setLoading] = useState(true);
  const [metaDataLoading, seMetaDatatLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rawCubes, setRawCubes] = useState([]);
  const { userContracts } = usePermissions();

  const allCubes = useMemo(() => {
    const allowedCubes = userContracts?.["tm1_cubes"];
    if (!allowedCubes || allowedCubes.length === 0) return rawCubes;
    return rawCubes.filter((cube) =>
      allowedCubes.some((allowed) => allowed.server === cube.server && allowed.name === cube.value)
    );
  }, [rawCubes, userContracts]);

  const [selectedCube, setSelectedCube] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [availableDimensions, setAvailableDimensions] = useState([]);
  const [elementsMap, setElementsMap] = useState({});
  const [rowDimensions, setRowDimensions] = useState([]);
  const [colDimensions, setColDimensions] = useState([]);
  const [selectedElements, setSelectedElements] = useState({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [currentFilterDimension, setCurrentFilterDimension] = useState(null);
  const [mdxData, setMdxData] = useState([]);
  const [columnStructure, setColumnStructure] = useState([]);
  const [isRunningMdx, setIsRunningMdx] = useState(false);
  const [mdxError, setMdxError] = useState(null);
  const [gridFeaturesDialogOpen, setGridFeaturesDialogOpen] = useState(false);
  const [gridFeatures, setGridFeatures] = useState(DEFAULT_GRID_FEATURES);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [percentRowsState, setPercentRowsState] = useState([]);
  const [percentColumnsState, setPercentColumnsState] = useState([]);
  const [currentLoadedViewId, setCurrentLoadedViewId] = useState(null);
  const [currentLoadedViewName, setCurrentLoadedViewName] = useState(null);

  // Calculated columns state
  const [calculatedColumns, setCalculatedColumns] = useState([]);
  const [calcColDialogOpen, setCalcColDialogOpen] = useState(false);
  const [editingCalcCol, setEditingCalcCol] = useState(null);

  // Hide Blank Columns toggle (off by default)
  const [hideBlankColumns, setHideBlankColumns] = useState(false);
  // Ref mirror so grid filter callbacks always read the latest toggle value
  const hideBlankColumnsRef = useRef(false);
  const collapsedRowKeysRef = useRef(new Set()); // hierarchy parents whose children are hidden

  const gridRef = useRef();
  // Grid popups (chart window, menus) render inside the page area, so a maximized chart
  // fills the space below the app header instead of the whole body behind it
  const [popupParentEl, setPopupParentEl] = useState(null);
  const hasFetchedRef = useRef(false);

  const { theme } = useTheme();
  const [gridThemeClass, setGridThemeClass] = useState(
    theme === "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"
  );
  const location = useLocation();
  const navigate = useNavigate();

  // ═══════════════════════════════════════════════════════════
  // ✅ autoConfig as state
  // ═══════════════════════════════════════════════════════════
  const [autoConfig, setAutoConfig] = useState(location.state || null);
  const hasAutoApplied = useRef(false);

  // ═══════════════════════════════════════════════════════════
  // ✅ Sync autoConfig when location.state changes
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (location.state) {
      setAutoConfig(location.state);
      hasAutoApplied.current = false;
    }
  }, [location.state, location.key]);

  // ═══════════════════════════════════════════════════════════
  // ✅ Auto-apply dimensions effect
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!autoConfig || hasAutoApplied.current || availableDimensions.length === 0) return;

    hasAutoApplied.current = true;

    if (autoConfig.dimensions?.rows) {
      const rowDims = autoConfig.dimensions.rows
        .map((fp) => fp.match(/^\[([^\]]+)\]/)?.[1])
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i);
      setRowDimensions(rowDims);
    }

    if (autoConfig.dimensions?.columns) {
      const colDims = autoConfig.dimensions.columns
        .map((fp) => fp.match(/^\[([^\]]+)\]/)?.[1])
        .filter(Boolean)
        .filter((v, i, arr) => arr.indexOf(v) === i);
      setColDimensions(colDims);
    }

    const allPaths = [
      ...(autoConfig.dimensions?.rows || []),
      ...(autoConfig.dimensions?.columns || []),
      ...(autoConfig.dimensions?.filters || []),
    ];

    const elementsToSet = {};
    allPaths.forEach((fullPath) => {
      const dimName = fullPath.match(/^\[([^\]]+)\]/)?.[1];
      if (!dimName) return;
      if (!elementsToSet[dimName]) elementsToSet[dimName] = [];
      elementsToSet[dimName].push(fullPath);
    });

    if (Object.keys(elementsToSet).length > 0) {
      setSelectedElements((prev) => ({ ...prev, ...elementsToSet }));
    }
  }, [availableDimensions, autoConfig]);

  // ═══════════════════════════════════════════════════════════
  // ✅ Auto cube match — ALWAYS force reload
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!autoConfig?.server || !autoConfig?.cube || allCubes.length === 0) return;

    const matchedCube = allCubes.find(
      (c) => c.value === autoConfig.cube && c.server === autoConfig.server
    );

    if (matchedCube) {
      hasAutoApplied.current = false;
      setAvailableDimensions([]);
      setSelectedCube(matchedCube);
      handleLoadData(matchedCube);
    }
  }, [allCubes, autoConfig, location.key]);

  useEffect(() => {
    setGridThemeClass(theme === "dark" ? "ag-theme-balham-dark" : "ag-theme-balham");
  }, [theme]);

  const useRowSpans = useMemo(() => rowDimensions.length === 1, [rowDimensions]);

  // ─── Custom Excel export ───
  const exportExcelWithMerge = useCallback(
    async (api) => {
      const fileName = `${selectedCube?.label || "export"}.xlsx`;
      const result = api.getDataAsExcel({ fileName, processCellCallback: getFormattedExportValue });
      if (!result || typeof result === "string") {
        api.exportDataAsExcel({ fileName });
        return;
      }

      try {
        const { default: JSZip } = await import("jszip");
        const zip = await JSZip.loadAsync(result);
        let xml = await zip.file("xl/worksheets/sheet1.xml")?.async("text");
        if (!xml) throw new Error("no sheet");

        const numDims = rowDimensions.length;
        const newMerges = [];

        const exportRows = [];
        api.forEachNodeAfterFilterAndSort((node) => {
          if (node.data) exportRows.push(node.data);
        });

        const findCell = (src, ref) => {
          const tag = `<c r="${ref}"`;
          const s = src.indexOf(tag);
          if (s === -1) return null;
          const sc = src.indexOf("/>", s);
          const ec = src.indexOf("</c>", s);
          if (ec !== -1 && (sc === -1 || ec < sc)) return { s, e: ec + 4 };
          if (sc !== -1) return { s, e: sc + 2 };
          return null;
        };

        const removeCell = (src, ref) => {
          const p = findCell(src, ref);
          return p ? src.slice(0, p.s) + src.slice(p.e) : src;
        };

        const headerRows = Math.max(1, getColumnDepth(columnDefs));

        const insertOrdered = (src, rowNum, colLetter, cellXml) => {
          const rowTag = `<row r="${rowNum}"`;
          const rowStart = src.indexOf(rowTag);

          if (rowStart === -1) {
            let before = src.indexOf("</sheetData>");
            for (let r = rowNum + 1; r <= rowNum + 200; r++) {
              const ri = src.indexOf(`<row r="${r}"`);
              if (ri !== -1 && ri < before) {
                before = ri;
                break;
              }
            }
            return src.slice(0, before) + `<row r="${rowNum}">${cellXml}</row>` + src.slice(before);
          }

          const tagEnd = src.indexOf(">", rowStart);

          if (src[tagEnd - 1] === "/") {
            return src.slice(0, tagEnd - 1) + `>${cellXml}</row>` + src.slice(tagEnd + 1);
          }

          const rowEnd = src.indexOf("</row>", tagEnd);
          const body = src.slice(tagEnd + 1, rowEnd);
          const cellRe = /<c r="([A-Z]+)\d+"/g;
          let insertOffset = body.length;
          let m;
          while ((m = cellRe.exec(body)) !== null) {
            if (m[1] > colLetter) {
              insertOffset = m.index;
              break;
            }
          }
          const abs = tagEnd + 1 + insertOffset;
          return src.slice(0, abs) + cellXml + src.slice(abs);
        };

        for (let d = 0; d < numDims; d++) {
          const L = String.fromCharCode(65 + d);
          const dimName = rowDimensions[d];

          const existingPos = findCell(xml, `${L}${headerRows}`);
          let styleAttr = "";
          if (existingPos) {
            const sm = xml.slice(existingPos.s, existingPos.e).match(/\bs="(\d+)"/);
            if (sm) styleAttr = ` s="${sm[1]}"`;
          }

          const esc = dimName.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
          for (let r = 1; r <= headerRows; r++) {
            xml = removeCell(xml, `${L}${r}`);
            const cellXml =
              r === 1
                ? `<c r="${L}1"${styleAttr} t="inlineStr"><is><t>${esc}</t></is></c>`
                : `<c r="${L}${r}"${styleAttr}/>`;
            xml = insertOrdered(xml, r, L, cellXml);
          }

          newMerges.push(`${L}1:${L}${headerRows}`);
        }

        const writeDimCell = (L, rowNum, val) => {
          const pos = findCell(xml, `${L}${rowNum}`);
          let styleAttr = "";
          if (pos) {
            const sm = xml.slice(pos.s, pos.e).match(/\bs="(\d+)"/);
            if (sm) styleAttr = ` s="${sm[1]}"`;
          }
          xml = removeCell(xml, `${L}${rowNum}`);
          const text = val === null || val === undefined ? "" : String(val);
          const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
          const cellXml = esc
            ? `<c r="${L}${rowNum}"${styleAttr} t="inlineStr"><is><t>${esc}</t></is></c>`
            : `<c r="${L}${rowNum}"${styleAttr}/>`;
          xml = insertOrdered(xml, rowNum, L, cellXml);
        };

        for (let d = 0; d < numDims; d++) {
          const L = String.fromCharCode(65 + d);
          let mergeStart = headerRows + 1;
          let curVal = exportRows[0]?.[`dim_${d}`];
          writeDimCell(L, mergeStart, curVal);

          for (let i = 1; i < exportRows.length; i++) {
            const val = exportRows[i][`dim_${d}`];
            let same = val === curVal;
            if (same && d > 0) {
              for (let p = 0; p < d; p++) {
                if (exportRows[i][`dim_${p}`] !== exportRows[i - 1][`dim_${p}`]) {
                  same = false;
                  break;
                }
              }
            }
            if (!same) {
              const end = headerRows + i;
              if (end > mergeStart) newMerges.push(`${L}${mergeStart}:${L}${end}`);
              mergeStart = headerRows + 1 + i;
              curVal = val;
              writeDimCell(L, mergeStart, curVal);
            }
          }
          const lastEnd = headerRows + exportRows.length;
          if (lastEnd > mergeStart) newMerges.push(`${L}${mergeStart}:${L}${lastEnd}`);
        }

        if (newMerges.length > 0) {
          const dimCols = new Set(rowDimensions.map((_, i) => String.fromCharCode(65 + i)));
          const existing = [];
          const em = xml.match(/<mergeCells[^>]*>([\s\S]*?)<\/mergeCells>/);
          if (em) {
            for (const m of em[1].matchAll(/ref="([^"]+)"/g)) {
              const col = m[1].match(/^([A-Z]+)/)?.[1];
              if (col && !dimCols.has(col)) existing.push(m[1]);
            }
          }
          const all = [...existing, ...newMerges];
          const mXml = `<mergeCells count="${all.length}">${all.map((r) => `<mergeCell ref="${r}"/>`).join("")}</mergeCells>`;
          xml = em
            ? xml.replace(/<mergeCells[^>]*>[\s\S]*?<\/mergeCells>/, mXml)
            : xml.replace("</sheetData>", "</sheetData>" + mXml);
        }

        zip.file("xl/worksheets/sheet1.xml", xml);
        const finalBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(finalBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Excel merge export failed:", e);
        api.exportDataAsExcel({ fileName });
      }
    },
    [selectedCube, rowDimensions, columnDefs]
  );

  useEffect(() => {
    setCurrentLoadedViewId(null);
  }, [selectedCube]);

  const availableFieldsForCalc = useMemo(() => {
    if (mdxData.length === 0) return [];
    return extractMeasures(mdxData);
  }, [mdxData]);

  const fetchCubes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall("get", "/tm1/metadata");
      const items = response?.data || [];
      const cubes = items.flatMap((serverItem) => {
        const server = serverItem.server;
        const cubeEntries = serverItem.data?.value || [];
        return cubeEntries
          .map((entry) => {
            if (/^[{}]/.test(entry?.Name)) return null;
            const name = (entry?.Name || "").replace(/_/g, " ").replace(/[{}]/g, "").trim();
            if (!name) return null;
            return { value: entry?.Name, label: `${name} (${server})`, server };
          })
          .filter(Boolean);
      });
      setRawCubes(cubes);
    } catch (err) {
      setError(`Failed to load cubes. ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchCubes();
  }, []);

  // ═══════════════════════════════════════════════════════════
  // ✅ handleLoadData — always works, even for same cube
  // ═══════════════════════════════════════════════════════════
  const handleLoadData = async (cubeToLoad = null) => {
    const cube = cubeToLoad || selectedCube;
    if (!cube) {
      toast.error("No cube selected");
      return;
    }

    // Ensure selectedCube state stays in sync
    if (cubeToLoad && cubeToLoad !== selectedCube) {
      setSelectedCube(cubeToLoad);
    }

    setIsFetchingData(true);
    setAvailableDimensions([]);
    setElementsMap({});
    setRowDimensions([]);
    setColDimensions([]);
    setSelectedElements({});
    setMdxData([]);
    setColumnStructure([]);
    setRowData([]);
    setColumnDefs([]);
    setCalculatedColumns([]);
    setError(null);
    try {
      seMetaDatatLoading(true);
      const response = await apiCall(
        "get",
        `/getCubeMetaWithEdges?server=${encodeURIComponent(cube.server)}&cube=${encodeURIComponent(cube?.value)}`,
        null,
        null,
        false,
        false
      );
      if (response.success === true) {
        const dimensions = response?.dimensions || [];
        const { dimensions: dimList, elements: elemMap } = dimensionsToPivotData(dimensions);
        setAvailableDimensions(dimList);
        setElementsMap(elemMap);
        const initialSelected = {};
        dimList.forEach((dim) => {
          const elements = elemMap[dim] || [];
          initialSelected[dim] = elements.length > 0 ? [elements[0].fullPath] : [];
        });
        setSelectedElements(initialSelected);
      } else {
        setError(response.message);
      }
    } catch (err) {
      if (err?.__aborted) return;
      setError(err?.message || "Failed to fetch metadata");
    } finally {
      setIsFetchingData(false);
      seMetaDatatLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // ✅ handleCubeChange — dropdown se cube select hone pe
  //    (handleLoadData ke BAAD define hona chahiye)
  // ═══════════════════════════════════════════════════════════
  const handleCubeChange = (cube) => {
    if (!cube) return;

    // Reset autoConfig so useEffect path is skipped
    setAutoConfig(null);
    hasAutoApplied.current = true;

    // Clear previous cube's data
    setAvailableDimensions([]);
    setSelectedCube(cube);

    // ✅ Force API call — same cube pe bhi chalega
    handleLoadData(cube);
  };

  const handleDragStart = (e, dimension) => {
    e.dataTransfer.setData("dimension", dimension);
  };

  const handleDrop = (dimension, type) => {
    if (type === "rows" && !rowDimensions.includes(dimension))
      setRowDimensions([...rowDimensions, dimension]);
    else if (type === "columns" && !colDimensions.includes(dimension))
      setColDimensions([...colDimensions, dimension]);
  };

  const handleRemove = (dimension, type, movingToAxis = false) => {
    if (type === "rows") setRowDimensions(rowDimensions.filter((d) => d !== dimension));
    else if (type === "columns") setColDimensions(colDimensions.filter((d) => d !== dimension));

    // Moving to the filter (where) bar keeps a single member: the first of the members already
    // selected for this dimension (in the dimension's member order), else the dimension's first
    if (!movingToAxis) {
      const elements = elementsMap[dimension] || [];
      if (elements.length > 0) {
        setSelectedElements((prev) => {
          const current = prev[dimension] || [];
          const firstSelected =
            elements.find((el) => current.includes(el.fullPath))?.fullPath ?? current[0];
          return { ...prev, [dimension]: [firstSelected ?? elements[0].fullPath] };
        });
      }
    }
  };

  const handleFilterClick = (dimension) => {
    const isInAxis = rowDimensions.includes(dimension) || colDimensions.includes(dimension);
    setCurrentFilterDimension({ name: dimension, isInAxis });
    setFilterDialogOpen(true);
  };

  const handleFilterSave = (dimension, selected) => {
    setSelectedElements((prev) => ({ ...prev, [dimension]: selected }));
  };

  useEffect(() => {
    if (availableDimensions.length === 0) {
      setMdxError(null);
      setMdxData([]);
      setColumnStructure([]);
      setRowData([]);
      setColumnDefs([]);
      return;
    }
    if (rowDimensions.length === 0 || colDimensions.length === 0) {
      setMdxError(null);
      setMdxData([]);
      setColumnStructure([]);
      setRowData([]);
      setColumnDefs([]);
      return;
    }
    runMdxQuery();
  }, [rowDimensions, colDimensions, selectedElements]);

  useEffect(() => {
    if (mdxData.length === 0 || rowDimensions.length === 0 || colDimensions.length === 0) return;
    const measures = extractMeasures(mdxData);
    const baseGridData = convertToAGGridFormat(
      mdxData,
      rowDimensions,
      columnStructure,
      measures,
      useRowSpans
    );
    const baseColDefs = generateColumnDefs(
      rowDimensions,
      columnStructure,
      measures,
      useRowSpans,
      percentRowsState,
      calculatedColumns,
      percentColumnsState
    );
    const { gridData: finalGridData, colDefs: finalColDefs } = applyCalculatedColumns(
      baseGridData,
      baseColDefs,
      calculatedColumns,
      columnStructure,
      measures
    );
    setRowData(finalGridData);
    setColumnDefs(finalColDefs);
  }, [
    calculatedColumns,
    mdxData,
    rowDimensions,
    columnStructure,
    useRowSpans,
    percentRowsState,
    percentColumnsState,
  ]);

  const runMdxQuery = async () => {
    if (!selectedCube || availableDimensions.length === 0) {
      toast.error("Please select a cube and load metadata first");
      return;
    }
    if (rowDimensions.length === 0 && colDimensions.length === 0) {
      toast.error("Please drag at least one dimension to Rows or Columns");
      return;
    }

    setIsRunningMdx(true);
    setMdxError(null);
    setMdxData([]);
    setColumnStructure([]);
    setRowData([]);
    setColumnDefs([]);
    collapsedRowKeysRef.current = new Set(); // new result → all hierarchy rows expanded

    try {
      const rowElements = [];
      rowDimensions.forEach((dim) => {
        const sel = selectedElements[dim] || [];
        rowElements.push(...sel);
      });

      const columnElements = [];
      colDimensions.forEach((dim) => {
        const sel = selectedElements[dim] || [];
        columnElements.push(...sel);
      });

      const usedDims = new Set([...rowDimensions, ...colDimensions]);
      const whereDimensions = availableDimensions.filter((dim) => !usedDims.has(dim));
      const whereElements = [];
      whereDimensions.forEach((dim) => {
        const sel = selectedElements[dim] || [];
        whereElements.push(...sel);
      });

      const payload = {
        server: selectedCube.server,
        cube: selectedCube.value,
        rows: rowElements,
        columns: columnElements,
        where: whereElements,
      };

      const response = await apiCall("post", MDX_QUERY_ENDPOINT, payload, null, false, false);

      if (isMdxV2Response(response)) {
        const {
          rows,
          columnStructure: colStruct,
          percentRows,
          percentColumns,
        } = flattenMdxV2Data(response);

        setMdxData(rows);
        setColumnStructure(colStruct);
        setPercentRowsState(percentRows);
        setPercentColumnsState(percentColumns);

        const measures = extractMeasures(rows);
        const baseGridData = convertToAGGridFormat(
          rows,
          rowDimensions,
          colStruct,
          measures,
          rowDimensions.length === 1
        );
        const baseColDefs = generateColumnDefs(
          rowDimensions,
          colStruct,
          measures,
          useRowSpans,
          percentRows,
          calculatedColumns,
          percentColumns
        );

        const { gridData: finalGridData, colDefs: finalColDefs } = applyCalculatedColumns(
          baseGridData,
          baseColDefs,
          calculatedColumns,
          colStruct,
          measures
        );

        setRowData(finalGridData);
        setColumnDefs(finalColDefs);
      } else {
        setMdxData([]);
        setColumnStructure([]);
        setRowData([]);
        setColumnDefs([]);
      }
    } catch (err) {
      if (err?.__aborted) return;
      setMdxError(`MDX Query failed: ${err.message || err}`);
    } finally {
      setIsRunningMdx(false);
    }
  };

  const handleReorder = (type, newOrder) => {
    if (type === "rows") setRowDimensions(newOrder);
    else if (type === "columns") setColDimensions(newOrder);
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: gridFeatures.sorting,
      filter: gridFeatures.filtering,
      resizable: true,
      floatingFilter: false,
      suppressMovable: false,
      enablePivot: gridFeatures.pivoting,
      enableValue: gridFeatures.aggregation,
      enableRowGroup: gridFeatures.rowGrouping,
      menuTabs: gridFeatures.columnMenu
        ? ["generalMenuTab", "filterMenuTab", "columnsMenuTab"]
        : [],
    }),
    [gridFeatures]
  );

  const onGridReady = useCallback((params) => {
    const applySmartSizing = () => {
      if (!params.api || !params.columnApi) return;
      const allColumns = params.columnApi.getAllDisplayedColumns();
      let totalColumnsWidth = 0;
      allColumns.forEach((col) => {
        totalColumnsWidth += col.getActualWidth();
      });
      const gridWidth = params.api.gridPanel?.eBodyViewport?.clientWidth;
      if (gridWidth && totalColumnsWidth < gridWidth - 50) {
        params.api.sizeColumnsToFit();
      } else {
        params.columnApi.autoSizeAllColumns({ skipHeader: false });
      }
    };
    setTimeout(applySmartSizing, 150);
    const handleResize = () => setTimeout(applySmartSizing, 100);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onSortChanged = useCallback(
    (params) => {
      const columnState = params.columnApi.getColumnState();
      const sortedCols = columnState.filter((col) => col.sort !== null);
      if (sortedCols.length === 0) return;
      const newState = [];
      rowDimensions.forEach((dim, index) => {
        const field = `dim_${index}`;
        const existing = sortedCols.find((c) => c.colId === field);
        newState.push({ colId: field, sort: existing ? existing.sort : "asc", sortIndex: index });
      });
      sortedCols.forEach((col) => {
        if (!rowDimensions.some((_, i) => `dim_${i}` === col.colId)) {
          newState.push({ colId: col.colId, sort: col.sort, sortIndex: newState.length });
        }
      });
      params.columnApi.applyColumnState({ state: newState, applyOrder: true });
      const currentRows = [];
      params.api.forEachNodeAfterFilterAndSort((node) => {
        currentRows.push(node.data);
      });
      const newRowsWithSpans = calculateRowSpans(currentRows, rowDimensions.length);
      params.api.applyTransaction({ update: newRowsWithSpans });
    },
    [rowDimensions]
  );

  // Blank-row hiding and collapsed hierarchy children via AG Grid external filter — rowData
  // itself is never modified. Callbacks are stable and read refs/row data only, so they never
  // hold a stale closure; AG Grid re-runs them itself whenever new rowData is applied.
  const isExternalFilterPresent = useCallback(
    () => hideBlankColumnsRef.current || collapsedRowKeysRef.current.size > 0,
    []
  );

  const doesExternalFilterPass = useCallback((node) => {
    if (!node.data) return true;
    if (hideBlankColumnsRef.current && isBlankDataRow(node.data)) return false;
    return !isUnderCollapsedRow(node.data, collapsedRowKeysRef.current);
  }, []);

  // Expand / collapse a parent row's hierarchy children
  const toggleRowCollapse = useCallback((rowKey, node) => {
    const next = new Set(collapsedRowKeysRef.current);
    if (next.has(rowKey)) next.delete(rowKey);
    else next.add(rowKey);
    collapsedRowKeysRef.current = next;

    const api = gridRef.current?.api;
    if (!api || api.isDestroyed?.()) return;
    api.onFilterChanged();
    if (node) api.redrawRows({ rowNodes: [node] }); // flip the chevron
  }, []);

  // Merged outer row-dimension cells (Contract …) are sized when a row renders, so after any
  // sort / filter / collapse / new data the rows are redrawn to re-merge in the new order.
  // Deferred so the grid finishes laying out the updated model first.
  const outerSpanRedrawTimerRef = useRef(null);
  const redrawOuterSpans = useCallback(
    (params) => {
      if (rowDimensions.length < 2) return;
      const api = params.api;
      clearTimeout(outerSpanRedrawTimerRef.current);
      outerSpanRedrawTimerRef.current = setTimeout(() => {
        if (!api || api.isDestroyed?.() || !api.getColumn("dim_0")?.isVisible()) return;
        api.redrawRows();
      }, 0);
    },
    [rowDimensions.length]
  );
  useEffect(() => () => clearTimeout(outerSpanRedrawTimerRef.current), []);

  // Stable grid context — read by HierarchyCellRenderer
  const gridContext = useMemo(
    () => ({
      isRowCollapsed: (rowKey) => collapsedRowKeysRef.current.has(rowKey),
      toggleRowCollapse,
    }),
    [toggleRowCollapse]
  );

  // Toggle changes — re-run the grid's filter pipeline
  useEffect(() => {
    hideBlankColumnsRef.current = hideBlankColumns;
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed?.()) api.onFilterChanged();
  }, [hideBlankColumns]);

  // ─── Dynamic chart — stays in sync with the table ───
  const chartOpenRef = useRef(false); // user wants the chart shown
  const chartRefRef = useRef(null); // current AG Grid ChartRef
  const rebuiltChartIdsRef = useRef(new Set()); // charts destroyed by a rebuild, not the user
  const chartSyncTimerRef = useRef(null);

  const destroyChartForRebuild = useCallback(() => {
    const chartRef = chartRefRef.current;
    chartRefRef.current = null;
    if (!chartRef) return;
    rebuiltChartIdsRef.current.add(chartRef.chartId);
    try {
      chartRef.destroyChart();
    } catch {
      rebuiltChartIdsRef.current.delete(chartRef.chartId);
    }
  }, []);

  const openDynamicChart = useCallback(
    (api) => {
      destroyChartForRebuild();
      chartRefRef.current = createDynamicChart(api);
      chartOpenRef.current = !!chartRefRef.current;
      if (!chartRefRef.current) toast.info("No chartable data in the current table");
    },
    [destroyChartForRebuild]
  );

  // Rebuild an open chart after rows/columns change (MDX refresh, dimensions, calc columns,
  // filters). Debounced so the burst of grid events from one update rebuilds only once.
  const syncOpenChart = useCallback(
    (params) => {
      if (!chartOpenRef.current) return;
      clearTimeout(chartSyncTimerRef.current);
      chartSyncTimerRef.current = setTimeout(() => {
        const api = params.api;
        if (!api || api.isDestroyed?.()) return;
        destroyChartForRebuild();
        chartRefRef.current = createDynamicChart(api);
      }, 0);
    },
    [destroyChartForRebuild]
  );

  const onChartDestroyed = useCallback((params) => {
    if (rebuiltChartIdsRef.current.delete(params.chartId)) return;
    if (chartRefRef.current?.chartId === params.chartId) chartRefRef.current = null;
    // Grid teardown (MDX re-run remount) also destroys the chart — keep it open for the new grid.
    // Only a close while the grid is still alive means the user dismissed it.
    setTimeout(() => {
      if (!params.api?.isDestroyed?.()) chartOpenRef.current = false;
    }, 0);
  }, []);

  useEffect(() => () => clearTimeout(chartSyncTimerRef.current), []);

  if (loading) return <LoadingComponent fullScreen />;
  if (error && allCubes.length === 0) return <ErrorState error={error} onRetry={fetchCubes} />;

  return (
    <div
      ref={setPopupParentEl}
      className="relative h-full flex-1 min-h-0 flex flex-col overflow-hidden p-1.5"
    >
      <div className="flex-1 min-h-0 flex flex-col gap-1.5">
        {/* Top Bar */}
        <div className="shrink-0 flex items-center justify-between gap-3 px-1.5 py-0.5 border-b bg-background rounded-lg shadow-sm">
          <div className="flex items-center gap-1.5 flex-1 max-w-md">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center shrink-0">
              <Database className="w-3.5 h-3.5 text-primary-foreground" />
            </div>
            <ReactSelect
              options={allCubes}
              value={selectedCube}
              onChange={handleCubeChange}
              placeholder="Search cubes..."
              isLoading={loading}
              className="flex-1 text-[12px]"
              controlHeight="30px"
            />
          </div>

          <PivotViewStore
            mode="full"
            currentViewData={{
              cube: selectedCube,
              rowDimensions,
              colDimensions,
              selectedElements,
              calculatedColumns,
            }}
            selectedCube={selectedCube}
            availableDimensions={availableDimensions}
            accessibleCubes={allCubes}
            onCubeChange={handleCubeChange}
            onApplyDimensions={(view) => {
              setRowDimensions(view.rowDimensions || []);
              setColDimensions(view.colDimensions || []);
              setSelectedElements(view.selectedElements || {});
              setCalculatedColumns(view.calculatedColumns || []);
            }}
            isSaveEnabled={
              !!selectedCube &&
              (rowDimensions.length > 0 || colDimensions.length > 0) &&
              !isRunningMdx &&
              mdxError === null &&
              rowData.length > 0
            }
            currentLoadedViewId={currentLoadedViewId}
            currentLoadedViewName={currentLoadedViewName}
            onViewLoaded={setCurrentLoadedViewId}
            onViewNameLoaded={setCurrentLoadedViewName}
          />
        </div>

        {!selectedCube && (
          <div className="flex-1 min-h-0">
            <EmptyStateNoCube />
          </div>
        )}
        {metaDataLoading && <LoadingComponent />}

        {selectedCube && (
          <>
            {error && <ErrorState error={error} variant="full" onRetry={handleLoadData} />}

            {availableDimensions.length > 0 && (
              <div className="flex-1 min-h-0 flex flex-col gap-1">
                <div className="flex-1 min-h-0 flex flex-col shadow-xl border-2 bg-background overflow-hidden">
                  {/* Available Dimensions */}
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dimension = e.dataTransfer.getData("dimension");
                      const sourceType = e.dataTransfer.getData("sourceType");
                      if (dimension && sourceType) handleRemove(dimension, sourceType, false);
                    }}
                    className="shrink-0 p-1.5 border-b-2 bg-muted/20 transition-all max-h-24 overflow-y-auto"
                  >
                    {(() => {
                      const unusedDimensions = availableDimensions.filter(
                        (dim) => !rowDimensions.includes(dim) && !colDimensions.includes(dim)
                      );
                      return (
                        <>
                          {unusedDimensions.length === 0 && (
                            <div className="flex items-center gap-1.5 mb-1">
                              <Database className="w-3.5 h-3.5 text-muted-foreground" />
                              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                Available Dimensions
                              </h3>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-1.5">
                            {unusedDimensions.map((dim) => (
                              <div key={dim} className="w-fit max-w-full">
                                <DraggableDimension
                                  dimension={dim}
                                  onDragStart={handleDragStart}
                                  onFilterClick={handleFilterClick}
                                  selectedElements={selectedElements}
                                  elementsMap={elementsMap}
                                />
                              </div>
                            ))}
                            {unusedDimensions.length === 0 && (
                              <p className="text-xs text-muted-foreground italic py-1">
                                All dimensions are currently in use
                              </p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="shrink-0 border-b-2 flex flex-row">
                    <div className="w-44 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <DropZone
                        bare
                        title="Columns"
                        items={colDimensions}
                        onDrop={handleDrop}
                        onRemove={handleRemove}
                        handleFilterClick={handleFilterClick}
                        type="columns"
                        onReorder={handleReorder}
                        className="max-h-20 overflow-y-auto"
                      />
                    </div>
                  </div>

                  <div className="flex flex-row flex-1 min-h-0">
                    {/* Left Controls Panel */}
                    <div className="flex flex-col space-y-1.5 w-44 shrink-0 overflow-y-auto border-r p-1.5">
                      <DropZone
                        bare
                        title="Rows"
                        items={rowDimensions}
                        onDrop={handleDrop}
                        onRemove={handleRemove}
                        handleFilterClick={handleFilterClick}
                        type="rows"
                        onReorder={handleReorder}
                        className="min-h-[270px]"
                      />

                      <div className="mt-auto space-y-1.5">
                        <div
                          className="flex items-center justify-between gap-2 px-2 py-1.5 border rounded-md bg-muted/20"
                          title="Hide value columns that are blank in every row"
                        >
                          <Label
                            htmlFor="hide-blank-columns"
                            className="text-xs font-medium cursor-pointer leading-tight"
                          >
                            Hide Blank Rows
                          </Label>
                          <Switch
                            id="hide-blank-columns"
                            checked={hideBlankColumns}
                            onCheckedChange={setHideBlankColumns}
                          />
                        </div>

                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full">
                              <Settings className="w-4 h-4 mr-2" />
                              Actions
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="w-[var(--radix-popover-trigger-width)] p-2 flex flex-col gap-1.5"
                          >
                            <Button
                              onClick={() => openDynamicChart(gridRef.current?.api)}
                              disabled={!rowData.length}
                              variant="outline"
                              size="sm"
                              className="w-full justify-start"
                            >
                              <BarChart className="w-4 h-4 mr-2" />
                              Show Chart
                            </Button>

                            <Button
                              onClick={() => setGridFeaturesDialogOpen(true)}
                              variant="outline"
                              size="sm"
                              className="w-full justify-start"
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              Grid Features
                            </Button>

                            <PivotViewStore
                              mode="save-only"
                              currentViewData={{
                                cube: selectedCube,
                                rowDimensions,
                                colDimensions,
                                selectedElements,
                                calculatedColumns,
                              }}
                              selectedCube={selectedCube}
                              availableDimensions={availableDimensions}
                              accessibleCubes={allCubes}
                              onCubeChange={handleCubeChange}
                              onApplyDimensions={(view) => {
                                setRowDimensions(view.rowDimensions || []);
                                setColDimensions(view.colDimensions || []);
                                setSelectedElements(view.selectedElements || {});
                                setCalculatedColumns(view.calculatedColumns || []);
                              }}
                              isSaveEnabled={
                                !!selectedCube &&
                                (rowDimensions.length > 0 || colDimensions.length > 0) &&
                                !isRunningMdx &&
                                mdxError === null &&
                                rowData.length > 0
                              }
                              currentLoadedViewId={currentLoadedViewId}
                              currentLoadedViewName={currentLoadedViewName}
                              onViewLoaded={setCurrentLoadedViewId}
                              onViewNameLoaded={setCurrentLoadedViewName}
                            />
                          </PopoverContent>
                        </Popover>

                        {/* Calculated Columns */}
                        {rowData.length > 0 && (
                          <div className="p-1.5 border-2 border-dashed rounded-lg bg-muted/20 border-amber-300 dark:border-amber-700">
                            <div className="flex items-center justify-center gap-1.5 mb-1">
                              <Calculator className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                              <h3 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider leading-tight">
                                Calculated Columns
                              </h3>
                            </div>
                            {calculatedColumns.length === 0 ? (
                              <div className="text-center py-1">
                                <p className="text-[10px] text-muted-foreground italic">
                                  No calculated columns yet
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {calculatedColumns.map((col) => (
                                  <div
                                    key={col.id}
                                    className="px-1.5 py-1 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-200 dark:border-amber-800"
                                  >
                                    <div className="flex items-start justify-between gap-1">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-xs font-semibold truncate text-amber-800 dark:text-amber-300">
                                          {col.name}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground font-mono truncate">
                                          {col.formula}
                                        </p>
                                      </div>
                                      <div className="flex gap-0.5 shrink-0">
                                        <button
                                          onClick={() => {
                                            setEditingCalcCol(col);
                                            setCalcColDialogOpen(true);
                                          }}
                                          className="p-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded transition-colors"
                                          title="Edit"
                                        >
                                          <Edit2 className="w-3 h-3 text-amber-700 dark:text-amber-400" />
                                        </button>
                                        <button
                                          onClick={() => {
                                            setCalculatedColumns((prev) =>
                                              prev.filter((c) => c.id !== col.id)
                                            );
                                            toast.success(`"${col.name}" removed`);
                                          }}
                                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded transition-colors"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-3 h-3 text-red-500" />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="flex justify-center mt-1">
                              <button
                                onClick={() => {
                                  setEditingCalcCol(null);
                                  setCalcColDialogOpen(true);
                                }}
                                className="flex items-center gap-1 text-[10px] px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors font-medium"
                              >
                                <Plus className="w-3 h-3" />
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0 min-h-0 flex flex-col">
                      {isRunningMdx && (
                        <div className="w-full flex-1 min-h-0 border-1 flex items-center justify-center">
                          <LoadingComponent message="Running MDX Query..." />
                        </div>
                      )}

                      {!isRunningMdx && rowData.length > 0 && columnDefs.length > 0 && (
                        <div className={`${gridThemeClass} w-full flex-1 min-h-0`}>
                          <AgGridReact
                            theme={gridThemeClass}
                            ref={gridRef}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            autoGroupColumnDef={{
                              headerName: "Group",
                              minWidth: 200,
                              pinned: "left",
                              cellClass: "excel-dim-cell",
                              headerClass: "excel-dim-header",
                            }}
                            onGridReady={onGridReady}
                            context={gridContext}
                            postSortRows={keepHierarchyOnSort}
                            onModelUpdated={redrawOuterSpans}
                            isExternalFilterPresent={isExternalFilterPresent}
                            doesExternalFilterPass={doesExternalFilterPass}
                            onFirstDataRendered={syncOpenChart}
                            onRowDataUpdated={syncOpenChart}
                            onNewColumnsLoaded={syncOpenChart}
                            onFilterChanged={syncOpenChart}
                            onChartDestroyed={onChartDestroyed}
                            onSortChanged={
                              gridFeatures.sorting && useRowSpans ? onSortChanged : undefined
                            }
                            animateRows={true}
                            rowSelection={gridFeatures.rowSelection ? "multiple" : undefined}
                            singleClickEdit={gridFeatures.singleClickEdit}
                            pagination={gridFeatures.pagination}
                            paginationPageSize={gridFeatures.pagination ? 50 : undefined}
                            statusBar={
                              gridFeatures.statusBar
                                ? {
                                    statusPanels: [
                                      { statusPanel: "agTotalAndFilteredRowCountComponent" },
                                      { statusPanel: "agTotalRowCountComponent" },
                                      { statusPanel: "agFilteredRowCountComponent" },
                                      { statusPanel: "agSelectedRowCountComponent" },
                                      { statusPanel: "agAggregationComponent" },
                                    ],
                                  }
                                : undefined
                            }
                            sideBar={
                              gridFeatures.sideBar
                                ? {
                                    toolPanels: [
                                      {
                                        id: "columns",
                                        labelDefault: "Columns",
                                        labelKey: "columns",
                                        iconKey: "columns",
                                        toolPanel: "agColumnsToolPanel",
                                      },
                                      {
                                        id: "filters",
                                        labelDefault: "Filters",
                                        labelKey: "filters",
                                        iconKey: "filter",
                                        toolPanel: "agFiltersToolPanel",
                                      },
                                    ],
                                  }
                                : false
                            }
                            rowGroupPanelShow={gridFeatures.rowGrouping ? "always" : "never"}
                            getContextMenuItems={
                              !gridFeatures.contextMenu
                                ? () => []
                                : (params) => [
                                    "copy",
                                    "copyWithHeaders",
                                    "copyWithGroupHeaders",
                                    "paste",
                                    "separator",
                                    "chartRange",
                                    "separator",
                                    {
                                      name: "Export",
                                      subMenu: [
                                        {
                                          name: "CSV Export",
                                          action: () =>
                                            params.api.exportDataAsCsv({
                                              fileName: `${selectedCube?.label || "export"}.csv`,
                                              processCellCallback: getFormattedExportValue,
                                            }),
                                        },
                                        {
                                          name: "Excel Export",
                                          action: () => exportExcelWithMerge(params.api),
                                        },
                                      ],
                                    },
                                  ]
                            }
                            popupParent={popupParentEl ?? document.body}
                            suppressRowTransform={true}
                            suppressCellFocus={false}
                            suppressColumnVirtualisation={false}
                            suppressRowVirtualisation={false}
                            enableRangeSelection={gridFeatures.rangeSelection}
                            enableCharts={gridFeatures.charts}
                            pivotMode={gridFeatures.pivoting}
                            excelStyles={EXCEL_STYLES}
                          />
                        </div>
                      )}

                      {!isRunningMdx &&
                        (rowDimensions.length === 0 || colDimensions.length === 0) && (
                          <div className="flex-1 min-h-0">
                            <EmptyStateNoDimensions />
                          </div>
                        )}

                      {mdxError && <ErrorState error={mdxError} variant="full" />}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {availableDimensions.length === 0 && !isFetchingData && selectedCube && !error && (
              <div className="text-center py-16">
                <LoadingComponent message="Loading cube metadata..." />
              </div>
            )}
          </>
        )}
      </div>

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        {currentFilterDimension && filterDialogOpen && (
          <FilterDialog
            dimension={currentFilterDimension.name}
            elements={elementsMap[currentFilterDimension.name] || []}
            selectedElements={selectedElements[currentFilterDimension.name] || []}
            onSave={handleFilterSave}
            onClose={() => setFilterDialogOpen(false)}
            open={filterDialogOpen}
            isInAxis={currentFilterDimension.isInAxis}
          />
        )}
      </Dialog>

      {/* Grid Features Dialog */}
      <GridFeaturesDialog
        open={gridFeaturesDialogOpen}
        onClose={() => setGridFeaturesDialogOpen(false)}
        features={gridFeatures}
        onSave={setGridFeatures}
      />

      {/* Calculated Column Dialog */}
      <CalculatedColumnDialog
        open={calcColDialogOpen}
        onClose={() => {
          setCalcColDialogOpen(false);
          setEditingCalcCol(null);
        }}
        onSave={(col) => {
          if (editingCalcCol) {
            setCalculatedColumns((prev) => prev.map((c) => (c.id === col.id ? col : c)));
            toast.success(`"${col.name}" updated`);
          } else {
            setCalculatedColumns((prev) => [...prev, col]);
            toast.success(`"${col.name}" added`);
          }
        }}
        availableFields={availableFieldsForCalc}
        editingCol={editingCalcCol}
      />
    </div>
  );
};

export default CubePage;